import { cellsFor, PIECES, PIECE_KINDS } from './pieces';
import {
  BOARD_HEIGHT,
  BOARD_WIDTH,
  type ActivePiece,
  type Board,
  type GameState,
  type PieceKind,
} from './types';

export type RandomSource = () => number;

interface UndoSnapshot {
  state: GameState;
  bag: PieceKind[];
}

export function createEmptyBoard(): Board {
  return Array.from({ length: BOARD_HEIGHT }, () => Array.from({ length: BOARD_WIDTH }, () => null));
}

export function canPlace(board: Board, piece: ActivePiece): boolean {
  return cellsFor(piece.kind, piece.rotation).every(({ x, y }) => {
    const boardX = piece.x + x;
    const boardY = piece.y + y;
    if (boardX < 0 || boardX >= BOARD_WIDTH || boardY >= BOARD_HEIGHT) return false;
    return boardY < 0 || board[boardY][boardX] === null;
  });
}

export function clearCompleteLines(board: Board): { board: Board; cleared: number } {
  const remaining = board.filter((row) => row.some((cell) => cell === null));
  const cleared = BOARD_HEIGHT - remaining.length;
  const emptyRows = Array.from({ length: cleared }, () => Array.from({ length: BOARD_WIDTH }, () => null));
  return { board: [...emptyRows, ...remaining.map((row) => [...row])], cleared };
}

export function dropIntervalFor(level: number): number {
  return Math.max(105, 820 - (level - 1) * 65);
}

function initialState(next: PieceKind): GameState {
  return {
    board: createEmptyBoard(),
    active: null,
    next,
    score: 0,
    lines: 0,
    level: 1,
    status: 'idle',
  };
}

export class GameEngine {
  state: GameState;
  private readonly random: RandomSource;
  private bag: PieceKind[] = [];
  private undoSnapshot: UndoSnapshot | undefined;

  constructor(random: RandomSource = Math.random, restored?: GameState) {
    this.random = random;
    this.state = restored ? this.cloneState(restored) : initialState(this.takeFromBag());
    if (this.state.status === 'playing') this.state.status = 'paused';
  }

  start(): void {
    this.bag = [];
    this.undoSnapshot = undefined;
    this.state = initialState(this.takeFromBag());
    this.state.status = 'playing';
    this.spawnNext();
  }

  togglePause(): void {
    if (this.state.status === 'playing') this.state.status = 'paused';
    else if (this.state.status === 'paused') this.state.status = 'playing';
  }

  move(horizontal: number): boolean {
    if (this.state.status !== 'playing' || !this.state.active) return false;
    const candidate = { ...this.state.active, x: this.state.active.x + horizontal };
    if (!canPlace(this.state.board, candidate)) return false;
    this.state.active = candidate;
    return true;
  }

  rotate(): boolean {
    if (this.state.status !== 'playing' || !this.state.active) return false;
    const rotations = PIECES[this.state.active.kind].length;
    const nextRotation = (this.state.active.rotation + 1) % rotations;
    const kicks = [0, -1, 1, -2, 2];
    for (const offset of kicks) {
      const candidate = { ...this.state.active, rotation: nextRotation, x: this.state.active.x + offset };
      if (canPlace(this.state.board, candidate)) {
        this.state.active = candidate;
        return true;
      }
    }
    const upward = { ...this.state.active, rotation: nextRotation, y: this.state.active.y - 1 };
    if (canPlace(this.state.board, upward)) {
      this.state.active = upward;
      return true;
    }
    return false;
  }

  tick(): boolean {
    return this.drop(false);
  }

  softDrop(): boolean {
    return this.drop(true);
  }

  private drop(awardPoint: boolean): boolean {
    if (this.state.status !== 'playing' || !this.state.active) return false;
    const candidate = { ...this.state.active, y: this.state.active.y + 1 };
    if (canPlace(this.state.board, candidate)) {
      this.state.active = candidate;
      if (awardPoint) this.state.score += 1;
      return true;
    }
    return false;
  }

  isGrounded(): boolean {
    if (!this.state.active) return false;
    return !canPlace(this.state.board, { ...this.state.active, y: this.state.active.y + 1 });
  }

  get canUndo(): boolean {
    return this.undoSnapshot !== undefined;
  }

  lock(): boolean {
    if (this.state.status !== 'playing' || !this.state.active || !this.isGrounded()) return false;
    this.undoSnapshot = {
      state: this.cloneState(this.state),
      bag: [...this.bag],
    };
    this.lockActive();
    return true;
  }

  undoLastLock(): boolean {
    if (!this.undoSnapshot) return false;
    this.state = this.cloneState(this.undoSnapshot.state);
    this.bag = [...this.undoSnapshot.bag];
    this.undoSnapshot = undefined;
    if (this.state.active) {
      this.state.active = {
        kind: this.state.active.kind,
        rotation: 0,
        x: 3,
        y: 0,
      };
    }
    return true;
  }

  private lockActive(): void {
    const active = this.state.active;
    if (!active) return;
    const board = this.state.board.map((row) => [...row]);
    for (const cell of cellsFor(active.kind, active.rotation)) {
      const x = active.x + cell.x;
      const y = active.y + cell.y;
      if (y < 0) {
        this.state.status = 'over';
        this.state.active = null;
        return;
      }
      board[y][x] = active.kind;
    }

    const result = clearCompleteLines(board);
    const scoreTable = [0, 100, 300, 500, 800];
    this.state.board = result.board;
    this.state.score += (scoreTable[result.cleared] ?? result.cleared * 250) * this.state.level;
    this.state.lines += result.cleared;
    this.state.level = Math.floor(this.state.lines / 10) + 1;
    this.spawnNext();
  }

  private spawnNext(): void {
    const kind = this.state.next;
    this.state.next = this.takeFromBag();
    const piece: ActivePiece = { kind, rotation: 0, x: 3, y: 0 };
    if (!canPlace(this.state.board, piece)) {
      this.state.active = null;
      this.state.status = 'over';
      return;
    }
    this.state.active = piece;
  }

  private takeFromBag(): PieceKind {
    if (this.bag.length === 0) {
      this.bag = [...PIECE_KINDS];
      for (let index = this.bag.length - 1; index > 0; index -= 1) {
        const swapWith = Math.floor(this.random() * (index + 1));
        [this.bag[index], this.bag[swapWith]] = [this.bag[swapWith], this.bag[index]];
      }
    }
    return this.bag.pop() ?? 'I';
  }

  private cloneState(state: GameState): GameState {
    return {
      ...state,
      board: state.board.map((row) => [...row]),
      active: state.active ? { ...state.active } : null,
    };
  }
}
